import React, { useEffect, useState, useCallback, useRef } from 'react';
import ComicPanel from './ComicPanel';
import '../styles/PanelStrip.css';

const PanelStrip = ({
  panels = [],
  autoScrollToLatest = true,
  actionArea = null
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Touch swipe detection
  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchEndRef = useRef({ x: 0, y: 0 });
  const MIN_SWIPE_DISTANCE = 50; // Minimum horizontal distance for a swipe

  // Auto-switch to latest panel when new panels are added
  useEffect(() => {
    if (autoScrollToLatest && panels.length > 0) {
      setCurrentIndex(panels.length - 1);
    }
  }, [panels.length, autoScrollToLatest]);

  // Navigate to previous panel
  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Scroll to top when changing panels
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentIndex]);

  // Navigate to next panel
  const goToNext = useCallback(() => {
    if (currentIndex < panels.length - 1) {
      setCurrentIndex(currentIndex + 1);
      // Scroll to top when changing panels
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentIndex, panels.length]);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    // Ignore multi-touch (pinch zoom) - let ThemeContext handle it
    if (e.touches.length > 1) return;
    
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  }, []);

  // Handle touch move - track end position
  const handleTouchMove = useCallback((e) => {
    // Ignore multi-touch (pinch zoom) - let ThemeContext handle it
    if (e.touches.length > 1) return;
    
    touchEndRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  }, []);

  // Handle touch end - determine if it was a horizontal swipe
  const handleTouchEnd = useCallback((e) => {
    // If there are still touches remaining, it was a multi-touch gesture - ignore
    if (e.touches.length > 0) return;
    
    const deltaX = touchStartRef.current.x - touchEndRef.current.x;
    const deltaY = touchStartRef.current.y - touchEndRef.current.y;
    
    // Only trigger swipe if horizontal movement is greater than vertical
    // This prevents swipe navigation when user is scrolling vertically
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > MIN_SWIPE_DISTANCE) {
      if (deltaX > 0) {
        // Swiped left - go to next panel
        goToNext();
      } else {
        // Swiped right - go to previous panel
        goToPrevious();
      }
    }
  }, [goToNext, goToPrevious]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  return (
    <div 
      className="panel-strip"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Comic book header/title area */}
      <div className="strip-header">
        <div className="header-line"></div>
        <h2 className="strip-title">YOUR STORY UNFOLDS...</h2>
        <div className="header-line"></div>
      </div>

      {/* Panel navigation indicators */}
      {panels.length > 1 && (
        <div className="panel-indicators">
          {panels.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to panel ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Panels container - only current panel is displayed */}
      <div className="panels-container">
        {panels.map((panel, index) => {
          const isLatest = index === panels.length - 1;
          const isCurrent = index === currentIndex;
          const showActionArea = isLatest && !panel.isGeneratingImage;
          
          return (
            <div
              key={panel.id}
              className={`panel-wrapper ${isCurrent ? 'current' : ''}`}
            >
              <div className="panel-content-scroll">
                <div>
                  <ComicPanel
                    id={panel.id}
                    type={panel.panelType || 'dialogue'}
                    imageUrl={panel.imageUrl}
                    isGeneratingImage={panel.isGeneratingImage}
                    narrator={panel.content}
                    playerAction={panel.playerAction || ''}
                    mood={panel.mood || 'mystery'}
                    isLatest={isLatest}
                  />
                  {/* Action area appears below the panel content on the latest panel, after image loads */}
                  {showActionArea && actionArea}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Panel counter */}
      {panels.length > 0 && (
        <div className="panel-counter">
          <span className="counter-current">{currentIndex + 1}</span>
          <span className="counter-separator">/</span>
          <span className="counter-total">{panels.length}</span>
        </div>
      )}

      {/* "To be continued" indicator when generating */}
      {panels.length > 0 && panels[panels.length - 1]?.isGeneratingImage && (
        <div className="strip-footer">
          <div className="footer-text">RENDERING...</div>
        </div>
      )}
    </div>
  );
};

export default PanelStrip;